import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import ChatWidget from "./components/ChatWidget";
import HomePage from "./pages/HomePage";
import EventPage from "./pages/EventPage";
import HowItWorks from "./pages/HowItWorks";
import PricingPage from "./pages/PricingPage";
import HelpCenter from "./pages/HelpCenter";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import CreateEventPage from "./pages/CreateEventPage";
import EventManagePage from "./pages/EventManagePage";
import EventUpgradePage from "./pages/EventUpgradePage";
import SeatFinderPage from "./pages/SeatFinderPage";
import FindMyPhotosPage from "./pages/FindMyPhotosPage";
import GuestGalleryPage from "./pages/GuestGalleryPage";
import AdminPage from "./pages/AdminPage";
import HighlightsPage from "./pages/HighlightsPage";
import SuperAlbumPublicPage from "./pages/SuperAlbumPublicPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import VendorPartnersPage from "./pages/VendorPartnersPage";
import PartnerCheckoutSuccessPage from "./pages/PartnerCheckoutSuccessPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import DataDeletionPolicyPage from "./pages/DataDeletionPolicyPage";
import AcceptableUsePolicyPage from "./pages/AcceptableUsePolicyPage";
import ContactPage from "./pages/ContactPage";
import AnalyticsLoader from "./components/AnalyticsLoader";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-navy-700/60">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  return children;
};

const Shell = ({ children, hideChrome }) => (
  <>
    {!hideChrome && <Header />}
    <main>{children}</main>
    {!hideChrome && <Footer />}
    {!hideChrome && <CookieBanner />}
    {!hideChrome && <ChatWidget />}
  </>
);

const RouteSwitch = () => {
  const loc = useLocation();
  // Synchronous OAuth callback detection (prevents race with existing session check)
  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isGuest = loc.pathname.startsWith("/g/") || loc.pathname.startsWith("/album/");
  return (
    <Shell hideChrome={isGuest}>
      <AnalyticsLoader />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/weddings" element={<EventPage />} />
        <Route path="/party" element={<EventPage />} />
        <Route path="/business" element={<EventPage />} />
        <Route path="/memorials" element={<EventPage />} />
        <Route path="/engagements" element={<EventPage />} />
        <Route path="/kids-parties" element={<EventPage />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/weddings-pricing" element={<PricingPage />} />
        <Route path="/engagements-pricing" element={<PricingPage />} />
        <Route path="/party-pricing" element={<PricingPage />} />
        <Route path="/business-pricing" element={<PricingPage />} />
        <Route path="/memorials-pricing" element={<PricingPage />} />
        <Route path="/kids-party-pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/data-deletion" element={<DataDeletionPolicyPage />} />
        <Route path="/data-deletion-policy" element={<DataDeletionPolicyPage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePolicyPage />} />
        <Route path="/acceptable-use-policy" element={<AcceptableUsePolicyPage />} />
        <Route path="/partners" element={<VendorPartnersPage />} />
        <Route path="/vendor-partners" element={<VendorPartnersPage />} />
        <Route path="/vendor-partners/success" element={<PartnerCheckoutSuccessPage />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/get-started" element={<AuthPage mode="register" />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/dashboard/new" element={<Protected><CreateEventPage /></Protected>} />
        <Route path="/dashboard/admin" element={<Protected><AdminPage /></Protected>} />
        <Route path="/dashboard/events/:id" element={<Protected><EventManagePage /></Protected>} />
        <Route path="/dashboard/events/:id/payment" element={<Protected><EventUpgradePage /></Protected>} />
        <Route path="/dashboard/events/:id/upgrade" element={<Protected><EventUpgradePage /></Protected>} />
        <Route path="/g/:slug" element={<GuestGalleryPage />} />
        <Route path="/g/:slug/highlights" element={<HighlightsPage />} />
        <Route path="/g/:slug/seat" element={<SeatFinderPage />} />
        <Route path="/g/:slug/find-me" element={<FindMyPhotosPage />} />
        <Route path="/album/:slug" element={<SuperAlbumPublicPage />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
    </Shell>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RouteSwitch />
          </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
